/*
 * Copyright © 2026 Medaica, Inc
 *
 * All rights reserved.
 *
 * This code is confidential and proprietary information belonging to Medaica, Inc.
 * Unauthorized copying, distribution, or use of this code, in whole or in part,
 * is strictly prohibited, and may constitute a violation of intellectual property rights.
 *
 * If you have received this code in error, please notify the owner immediately
 * at support@medaica.com and delete this file from your system.
 */

/* eslint-disable @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-argument --
 * The report document is loose-typed by design (vendored prototype JS core
 * with a hand-written surface .d.ts). */
/* eslint-disable @typescript-eslint/no-unsafe-return -- same rationale */

import React, { ReactElement, useState } from "react"
// @ts-ignore vendored plain-JS core with hand-written surface types
import { getQuestionnaireFlags } from "./core"
import { providerQuestionnaireOf } from "./provider-report-document"

/**
 * Patient Questionnaire panel — port of the report's renderQuestionnaire()
 * (cardiac_report_v0_5_6_dev_2.html 5822-5912, 5693-5703): red/amber
 * summary chips via the extracted getQuestionnaireFlags (red "Yes" chips
 * sorted first), the collapsible Q&A body with the prototype's answer-pill
 * classes (unanswered items dropped from the body), the ▼ toggle, and the
 * print-only full flag list (screen chips + body are print-hidden; the flag
 * list prints).
 *
 * Deviation (recorded in the parity note): no ResizeObserver chip-fitting /
 * "+N details" overflow — chips wrap; "View details" shows only with zero
 * chips, as upstream.
 */

const answerClass = (answer: string | null | undefined): string => {
  if (!answer) return "q-na"
  const a = answer.toLowerCase()
  if (a === "no" || a === "no change") return "q-no"
  if (a === "yes") return "q-yes"
  if (a === "not sure" || a === "not answered") return "q-concern"
  const concernAnswers = [
    "much worse",
    "both",
    "longer than 10 minutes",
    "more than once",
    "within last 3 months",
    "worsens during day",
  ]
  if (concernAnswers.includes(a)) return "q-yes"
  return "q-neutral"
}

const HeartReportQuestionnaire = ({ questionnaire }: { questionnaire: any }): ReactElement | null => {
  const [open, setOpen] = useState(false)
  // The red-first chips and the answered questions by section — shared with
  // the builder of the provider report PDF's data; null when there is nothing
  // to show (no questionnaire, or no answered question)
  const content = providerQuestionnaireOf(questionnaire)
  if (!content) {
    return null
  }
  const { flags: prioritized, sections } = content
  // The print-only list keeps the flags in questionnaire order
  const flags = getQuestionnaireFlags(questionnaire) as { text: string; answer: string; level: string }[]

  return (
    <div className="questionnaire-panel" data-testid="heart-report-questionnaire">
      <div
        className="questionnaire-header"
        role="button"
        tabIndex={0}
        title="Patient reported · not independently verified"
        aria-label="Patient Questionnaire. Patient reported and not independently verified. Select to view details."
        onClick={() => setOpen((current) => !current)}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault()
            setOpen((current) => !current)
          }
        }}
      >
        <h3>
          <span>Patient Questionnaire:</span>
          <span className="questionnaire-summary-chips phr-screen-only">
            {prioritized.map((flag) => (
              <span
                key={flag.text}
                className={`q-flag q-summary-flag ${flag.level}`}
                aria-label={`${flag.text}: ${flag.answer}`}
              >
                {flag.text}
              </span>
            ))}
            {prioritized.length === 0 && <span className="q-more">View details</span>}
          </span>
        </h3>
        <span className={`q-toggle${open ? " open" : ""}`}>▼</span>
      </div>
      {flags.length > 0 && (
        <div className="q-flags phr-print-only" aria-label="Patient-reported questionnaire responses">
          {flags.map((flag) => (
            <span key={flag.text} className={`q-flag ${flag.level}`} aria-label={`${flag.text}: ${flag.answer}`}>
              {flag.text}
            </span>
          ))}
        </div>
      )}
      {open && (
        <div className="questionnaire-body">
          {sections.map((section, index) => (
            // Titles repeat within a questionnaire (v2 "Race and Ethnicity" has
            // a checkbox row and a free-text row), so position keys the section
            <div className="q-section" key={`${section.title}-${index}`}>
              <div className="q-section-title">{section.title}</div>
              {section.items.map((item, itemIndex) => (
                <div className="q-item" key={`${item.question}-${itemIndex}`}>
                  <span className="q-question">{item.question}</span>
                  <span className={`q-answer ${answerClass(item.answer)}`}>{item.answer}</span>
                </div>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

export default HeartReportQuestionnaire
